<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { PhoneNumber } from '@tools/phoneNumber';
import { SUBSCRIPTION_TITLE_MAX_LENGTH, useSubscriptionStore } from '@organisms/ubiq-administration/store/subscriptions';
import { useManagePayAccount } from '@organisms/ubiq-administration/store/managePayAccount';
import { trans } from '../../../../_00_elements/translation';
import PhoneInput from '../../../../_01_atoms/components/PhoneInput.vue';
import Panel from '../../../../_02_molecules/components/Panel.vue';
import InfosCustomerField from './InfosCustomerField';

const managePayAccount = useManagePayAccount();

const emit = defineEmits([ 'next-step' ]);
const subscriptionStore = useSubscriptionStore();

const { errors, invoice, clientInvoiceData } = storeToRefs(subscriptionStore);
const { addError, clearError, getError, hasError } = subscriptionStore;
const phoneTouched = ref(false);

const phoneNumber = computed({
  get: () => new PhoneNumber(clientInvoiceData.value.phoneNumber ?? ''),
  set: (val) => {
    clientInvoiceData.value.phoneNumber = val?.international;
  },
});

const validationRules = {
  email: {
    validator: (value) => /^\w+([+.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/i.test(value),
    requiredMessage: trans('gestion.subscription.step.3.email.error.required'),
    errorMessage: trans('gestion.subscription.step.3.email.error.invalid'),
  },
  phoneNumber: {
    validator: () => phoneNumber.value?.isValid,
    errorMessage: trans('gestion.subscription.step.3.phone.invalid'),
  },
  invoiceTitle: {
    validator: (value) => !!value && value.length <= SUBSCRIPTION_TITLE_MAX_LENGTH,
    requiredMessage: trans('gestion.subscription.step.3.invoices.error.required'),
  },
  legalName: {
    validator: (value) => !!value,
    requiredMessage: trans('gestion.subscription.step.3.company.error.required'),
  },
  siren: {
    validator: (value) => /^\d{9}$/.test(value),
    requiredMessage: trans('gestion.subscription.step.3.siren.error.required'),
    errorMessage: trans('gestion.subscription.step.3.siren.error.invalid'),
  },
  vatNumber: {
    validator: (value) => {
      const v = String(value ?? '').trim().replace(/\s+/g, '').toUpperCase();
      return /^FR[A-Z0-9]{2}\d{9}$/.test(v);
    },
    requiredMessage: trans('gestion.subscription.step.3.vat.error.required'),
    errorMessage: trans('gestion.pay.informations.vat.error.invalid'),
  },
  streetAndNumber: {
    validator: (value) => !!value,
    requiredMessage: trans('gestion.subscription.step.3.address.error.required'),
  },
  zipCode: {
    validator: (value) => /^\d{5}$/.test(value),
    requiredMessage: trans('gestion.subscription.step.3.general.error.required'),
    errorMessage: trans('gestion.subscription.step.3.postal_code.error.invalid'),
  },
  city: {
    validator: (value) => !!value,
    requiredMessage: trans('gestion.subscription.step.3.general.error.required'),
  },
};

const { calculateVATNumber } = managePayAccount;

const validateField = ({ name, value, isTouched }) => {
  if (!name) {
    return false;
  }

  const rule = validationRules[name];

  if (!rule) {
    return true;
  }

  if (!value) {
    if (isTouched) {
      addError(name, rule?.requiredMessage ?? trans('global.error'));
    }

    return false;
  }

  if (name === 'siren' && rule.validator(value)) {
    const vatNumber = calculateVATNumber(value);

    if (vatNumber && !clientInvoiceData.value.vatNumber) {
      clientInvoiceData.value.vatNumber = vatNumber;
    }
  }

  if (rule.validator && !rule.validator(value)) {
    if (isTouched) {
      addError(name, rule?.errorMessage);
    }

    return false;
  }

  clearError(name);
  return true;
};

const getValue = (name) => (name === 'invoiceTitle'
  ? (invoice.value?.invoiceTitle ?? '')
  : (clientInvoiceData.value?.[name] ?? null));

const isNextDisabled = computed(() => {
  Object.keys(validationRules).forEach((name) => {
    validateField({ name, value: getValue(name) });
  });

  return Object.values(errors.value ?? {}).some(Boolean);
});

const displayKnownCustomerPanel = computed(() => !!subscriptionStore.clientInvoiceData.payOrganizationBuyerId);

const onClickNext = () => {
  if (isNextDisabled.value) {
    return;
  }

  emit('next-step');
};
</script>

<template>
  <div class="stepper-subscription infos-customer">
    <h3 class="stepper-subscription__title">
      {{ trans('gestion.subscription.step.3.title') }}
    </h3>
    <panel
      v-if="!displayKnownCustomerPanel"
      type="info"
      :content="trans('gestion.subscription.step.3.panel.is_client')"
      icon="ph_lightbulb-fill_blue"
    />
    <panel
      v-if="displayKnownCustomerPanel"
      type="warning"
      :content="trans('gestion.subscription.step.3.panel.is_not_client', { company_name: $companyName })"
      :title="trans('gestion.subscription.step.3.panel.is_not_client.title', { company_name: $companyName })"
      icon="ph_warning-fill_yellow"
    />
    <div class="infos-customer__form">
      <InfosCustomerField
        v-model="invoice.invoiceTitle"
        name="invoiceTitle"
        :maxlength="SUBSCRIPTION_TITLE_MAX_LENGTH"
        label="gestion.subscription.step.3.invoices"
        type="text"
        @blur="validateField"
      />
      <InfosCustomerField
        v-model="clientInvoiceData.legalName"
        name="legalName"
        label="gestion.subscription.step.3.company"
        type="text"
        :disabled="!!clientInvoiceData.payOrganizationBuyerId"
        @blur="validateField"
      />
      <InfosCustomerField
        v-model="clientInvoiceData.streetAndNumber"
        name="streetAndNumber"
        label="gestion.subscription.step.3.address"
        type="text"
        :disabled="!!clientInvoiceData.payOrganizationBuyerId"
        @blur="validateField"
      />
      <InfosCustomerField
        v-model="clientInvoiceData.additionalInfo"
        name="additionalInfo"
        label="gestion.subscription.step.3.address2"
        type="text"
        :disabled="!!clientInvoiceData.payOrganizationBuyerId"
        @blur="validateField"
      />
      <div class="infos-customer__row">
        <InfosCustomerField
          v-model="clientInvoiceData.zipCode"
          name="zipCode"
          label="gestion.subscription.step.3.postal_code"
          type="text"
          :disabled="!!clientInvoiceData.payOrganizationBuyerId"
          placeholder="75001"
          @blur="validateField"
        />
        <InfosCustomerField
          v-model="clientInvoiceData.city"
          name="city"
          label="gestion.subscription.step.3.city"
          type="text"
          :disabled="!!clientInvoiceData.payOrganizationBuyerId"
          @blur="validateField"
        />
      </div>
      <InfosCustomerField
        v-model="clientInvoiceData.siren"
        name="siren"
        label="gestion.subscription.step.3.siren"
        type="text"
        :disabled="!!clientInvoiceData.payOrganizationBuyerId"
        placeholder="123456789"
        @blur="validateField"
      />
      <InfosCustomerField
        v-model="clientInvoiceData.vatNumber"
        name="vatNumber"
        label="gestion.subscription.step.3.vat"
        type="text"
        :disabled="!!clientInvoiceData.payOrganizationBuyerId"
        placeholder="FR32123456789"
        @blur="validateField"
      />
      <InfosCustomerField
        v-model="clientInvoiceData.email"
        name="email"
        label="gestion.subscription.step.3.email"
        type="email"
        :disabled="!!clientInvoiceData.payOrganizationBuyerId"
        @blur="validateField"
      />
      <InfosCustomerField
        name="phoneNumber"
        label="gestion.subscription.step.3.phone"
      >
        <template #input>
          <PhoneInput
            v-model="phoneNumber"
            class="manage__input"
            :class="{ 'error': phoneTouched && !phoneNumber.isValid }"
            :disabled="!!clientInvoiceData.payOrganizationBuyerId"
            @blur="phoneTouched = true; validateField({ name: 'phoneNumber', value: phoneNumber, isTouched: phoneTouched })"
          />
          <span v-if="hasError('phoneNumber')" class="error-message">
            {{ getError('phoneNumber') }}
          </span>
        </template>
      </InfosCustomerField>
    </div>
    <div class="stepper-subscription__mandatory-infos">
      {{ trans('gestion.subscription.general.mandatory_fields') }}
    </div>
    <button
      class="btn btn--primary stepper-subscription__button"
      :disabled="isNextDisabled"
      @click="onClickNext"
    >
      {{ trans('global.pagination.next') }}
    </button>
  </div>
</template>
